export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  variables?: string[];
}

export type TemplateVariables = Record<string, string>;

class EmailTemplateService {
  private templates: EmailTemplate[] = [
    {
      id: 'welcome',
      name: 'Welcome Email',
      description: 'A warm welcome email for new contacts',
      category: 'Greetings',
      subject: 'Welcome to {{company_name}}!',
      htmlBody: `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <h1 style="color: #FDE047;">Welcome {{recipient_name}}!</h1>
    <p>Hi {{recipient_name}},</p>
    <p>We're excited to have you join us at {{company_name}}. {{additional_message}}</p>
    <p>If you have any questions, feel free to reach out to us anytime.</p>
    <p>Best regards,<br/>{{sender_name}}</p>
  </div>
</body>
</html>`,
      textBody: `Welcome {{recipient_name}}!

Hi {{recipient_name}},

We're excited to have you join us at {{company_name}}. {{additional_message}}

If you have any questions, feel free to reach out to us anytime.

Best regards,
{{sender_name}}`,
      variables: ['recipient_name', 'company_name', 'additional_message', 'sender_name']
    },
    {
      id: 'follow-up',
      name: 'Follow-up Email',
      description: 'Professional follow-up email',
      category: 'Professional',
      subject: 'Following up: {{topic}}',
      htmlBody: `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <h2 style="color: #FDE047;">Following Up</h2>
    <p>Hi {{recipient_name}},</p>
    <p>I wanted to follow up on our previous conversation about {{topic}}.</p>
    <p>{{follow_up_details}}</p>
    <p>Let me know your thoughts, and we can schedule a time to discuss further.</p>
    <p>Best regards,<br/>{{sender_name}}</p>
  </div>
</body>
</html>`,
      textBody: `Following Up

Hi {{recipient_name}},

I wanted to follow up on our previous conversation about {{topic}}.

{{follow_up_details}}

Let me know your thoughts, and we can schedule a time to discuss further.

Best regards,
{{sender_name}}`,
      variables: ['recipient_name', 'topic', 'follow_up_details', 'sender_name']
    },
    {
      id: 'meeting-request',
      name: 'Meeting Request',
      description: 'Professional meeting request email',
      category: 'Professional',
      subject: 'Meeting Request: {{meeting_title}}',
      htmlBody: `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <h2 style="color: #FDE047;">Meeting Request</h2>
    <p>Hi {{recipient_name}},</p>
    <p>I would like to schedule a meeting to discuss {{meeting_title}}.</p>
    <p><strong>Proposed Time:</strong> {{meeting_time}}</p>
    <p><strong>Duration:</strong> {{duration}}</p>
    <p><strong>Details:</strong> {{meeting_details}}</p>
    <p>Please let me know if this time works for you, or feel free to suggest an alternative.</p>
    <p>Best regards,<br/>{{sender_name}}</p>
  </div>
</body>
</html>`,
      textBody: `Meeting Request

Hi {{recipient_name}},

I would like to schedule a meeting to discuss {{meeting_title}}.

Proposed Time: {{meeting_time}}
Duration: {{duration}}
Details: {{meeting_details}}

Please let me know if this time works for you, or feel free to suggest an alternative.

Best regards,
{{sender_name}}`,
      variables: ['recipient_name', 'meeting_title', 'meeting_time', 'duration', 'meeting_details', 'sender_name']
    },
    {
      id: 'thank-you',
      name: 'Thank You Email',
      description: 'Express gratitude after a meeting or collaboration',
      category: 'Greetings',
      subject: 'Thank you for {{reason}}',
      htmlBody: `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <h2 style="color: #FDE047;">Thank You!</h2>
    <p>Hi {{recipient_name}},</p>
    <p>I wanted to personally thank you for {{reason}}. {{additional_message}}</p>
    <p>It was great working with you, and I look forward to future opportunities to collaborate.</p>
    <p>Best regards,<br/>{{sender_name}}</p>
  </div>
</body>
</html>`,
      textBody: `Thank You!

Hi {{recipient_name}},

I wanted to personally thank you for {{reason}}. {{additional_message}}

It was great working with you, and I look forward to future opportunities to collaborate.

Best regards,
{{sender_name}}`,
      variables: ['recipient_name', 'reason', 'additional_message', 'sender_name']
    },
    {
      id: 'project-update',
      name: 'Project Update',
      description: 'Send project status updates',
      category: 'Professional',
      subject: 'Project Update: {{project_name}}',
      htmlBody: `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <h2 style="color: #FDE047;">Project Update</h2>
    <p>Hi {{recipient_name}},</p>
    <p><strong>Project:</strong> {{project_name}}</p>
    <p><strong>Current Status:</strong> {{status}}</p>
    <p><strong>Completed:</strong> {{completed_items}}</p>
    <p><strong>Next Steps:</strong> {{next_steps}}</p>
    <p>Please feel free to reach out if you have any questions or concerns.</p>
    <p>Best regards,<br/>{{sender_name}}</p>
  </div>
</body>
</html>`,
      textBody: `Project Update

Hi {{recipient_name}},

Project: {{project_name}}
Current Status: {{status}}
Completed: {{completed_items}}
Next Steps: {{next_steps}}

Please feel free to reach out if you have any questions or concerns.

Best regards,
{{sender_name}}`,
      variables: ['recipient_name', 'project_name', 'status', 'completed_items', 'next_steps', 'sender_name']
    },
  ];

  getAllTemplates(): EmailTemplate[] {
    return this.templates;
  }

  getTemplatesByCategory(category: string): EmailTemplate[] {
    return this.templates.filter(t => t.category === category);
  }

  getCategories(): string[] {
    const categories = new Set(this.templates.map(t => t.category));
    return Array.from(categories).sort();
  }

  getTemplate(id: string): EmailTemplate | undefined {
    return this.templates.find(t => t.id === id);
  }

  getTemplateVariables(templateId: string): string[] {
    const template = this.getTemplate(templateId);
    return template?.variables || [];
  }

  fillTemplate(template: EmailTemplate, variables: TemplateVariables): {
    subject: string;
    htmlBody: string;
    textBody: string;
  } {
    let subject = template.subject;
    let htmlBody = template.htmlBody;
    let textBody = template.textBody;

    Object.entries(variables).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      subject = subject.replace(new RegExp(placeholder, 'g'), value);
      htmlBody = htmlBody.replace(new RegExp(placeholder, 'g'), value);
      textBody = textBody.replace(new RegExp(placeholder, 'g'), value);
    });

    return { subject, htmlBody, textBody };
  }
}

export const emailTemplateService = new EmailTemplateService();
